import { ethers } from "ethers";

export const USD_DECIMALS = 18;

export const shortAddress = (v) => v ? `${v.slice(0, 6)}...${v.slice(-4)}` : "—";

export const formatRaw = (v, decimals = 18, digits = 2) => {
  try {
    return Number(ethers.formatUnits(v || 0n, decimals)).toLocaleString("en-GB", {
      maximumFractionDigits: digits,
    });
  } catch {
    return "0";
  }
};

export const formatInteger = (v) =>
  Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

export const formatDate = (v) =>
  Number(v || 0) ? new Date(Number(v) * 1000).toLocaleString("en-GB") : "—";

export const tierLabel = (v) => Number(v || 0) ? `Tier ${Number(v)}` : "Unassigned";

export const getError = (e) =>
  e?.shortMessage || e?.reason || e?.info?.error?.message || e?.message || "Something went wrong";

export const tierStatus = (tier) => {
  if (!tier) return { label: "Inactive", cls: "border-white/10 bg-white/[0.03] text-slate-400" };
  if (tier.claimable) return { label: "Claimable", cls: "border-blue-400/20 bg-blue-400/10 text-blue-300" };
  if (tier.pending) return { label: "Pending", cls: "border-[#ffcc66]/20 bg-[#ffcc66]/10 text-[#ffcc66]" };
  return { label: "Active", cls: "border-emerald-400/20 bg-emerald-400/10 text-emerald-300" };
};

export function Page({ eyebrow, title, description, action, children }) {
  return (
    <section className="min-h-[calc(100vh-74px)] bg-[#050b08] px-4 py-8 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1100px]">
        <div className="mb-7 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ffcc66]">{eyebrow}</p>
            <h1 className="mt-2 text-3xl font-black md:text-4xl">{title}</h1>
            {description && <p className="mt-3 max-w-2xl text-sm leading-6 text-[#777]">{description}</p>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </section>
  );
}

export function Panel({ children, className = "" }) {
  return <div className={`rounded-[16px] border border-white/[0.08] bg-[#111314] p-5 ${className}`}>{children}</div>;
}

export function Stat({ label, value, note, accent = false }) {
  return (
    <div className={`rounded-[14px] border p-5 ${accent ? "border-[#ffcc66]/20 bg-[#ffcc66]/[0.04]" : "border-white/[0.08] bg-[#111314]"}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#747474]">{label}</p>
      <p className={`mt-3 break-words text-xl font-black ${accent ? "text-[#ffcc66]" : "text-white"}`}>{value}</p>
      {note && <p className="mt-2 text-[11px] text-[#666]">{note}</p>}
    </div>
  );
}

export function Progress({ value, max }) {
  const current = Number(value || 0);
  const maximum = Number(max || 0);
  const pct = maximum ? Math.min((current / maximum) * 100, 100) : 0;

  return (
    <div>
      <div className="mb-2 flex justify-between text-[11px] text-[#888]">
        <span>{formatInteger(current)} / {formatInteger(maximum)}</span>
        <span className="text-[#ffcc66]">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-black/50">
        <div className="h-full rounded-full bg-gradient-to-r from-[#ffcc66] to-[#b88a37]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
